import React, { useMemo, useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  Stack,
  Tabs,
  Tab,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
  TableSortLabel,
  CircularProgress,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  Award,
  Calendar,
  CreditCard,
  DollarSign,
  Download,
  ExternalLink,
  History,
  Hourglass,
  MoreHorizontal,
  Pause,
  Play,
  Receipt,
  UserCheck,
  Search
} from 'lucide-react';
import dayjs from 'dayjs';
import CustomPagination from '@components/Common/table/CustomPagination';
import { useModalDisclosure } from '@hooks/useDisclosure';
import { failed, success } from '@components/Common/Toastify';
import { defaultParams } from '@utils/global.constants';
import {
  SubscriptionPlanDto,
  UserSubscriptionDto,
  useAdminPlansQuery,
  useAssignPlanToUserMutation,
  useReactivateUserSubscriptionMutation,
  useSuspendUserSubscriptionMutation,
  useUserSubscriptionsQuery,
  useGrantGoodwillCreditsMutation,
  useExtendSubscriptionMutation,
  useExtendTrialMutation,
  useGrantTrialAgainMutation,
  useUserSpendingQuery,
  useUserInvoicesQuery,
  useUserSubscriptionHistoryQuery,
  useAdminStatsQuery,
  useUserUsageQuery,
} from '../../API/Subscriptions';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-sub-tabpanel-${index}`}
      aria-labelledby={`user-sub-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const getPlanNameFromInvoice = (inv: any) => {
  const lineItem = inv.lines?.data?.[0];
  if (!lineItem) return '—';
  const name = lineItem.price?.nickname || lineItem.plan?.nickname || lineItem.description;
  if (!name) return '—';
  if (name.toLowerCase().includes('starter')) return 'starter';
  if (name.toLowerCase().includes('pro')) return 'pro';
  if (name.toLowerCase().includes('enterprise')) return 'enterprise';
  return name;
};

const getEventChip = (eventType: string) => {
  let label = eventType.replace(/_/g, ' ');
  
  if (eventType === 'subscription_extended_admin') {
    label = 'Subscription Extended';
  } else if (eventType === 'trial_extended_admin') {
    label = 'Trial Extended';
  } else if (eventType === 'trial_regranted_admin') {
    label = 'Trial Regranted';
  } else if (eventType === 'subscription_assigned_admin') {
    label = 'New Plan Assigned';
  }
  
  return (
    <Chip
      label={label}
      color="default"
      size="small"
      variant="outlined"
      sx={{
        fontSize: '0.65rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        borderRadius: '6px',
        height: '24px'
      }}
    />
  );
};

const renderDetails = (log: any, plans: SubscriptionPlanDto[]) => {
  if (log.eventType === 'subscription_assigned_admin') {
    const planName = plans.find(p => p.id === log.newState?.planId)?.name || 'Standard Plan';
    const expDate = log.newState?.endsAt ? dayjs(log.newState.endsAt).format('MMM DD, YYYY') : 'Never';
    return (
      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
        Changed subscription to <Box component="span" sx={{ fontWeight: 600 }}>"{planName}"</Box> expiring on <Box component="span" sx={{ fontWeight: 600 }}>{expDate}</Box>. Reason: <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{log.newState?.reason || 'N/A'}</Box>
      </Typography>
    );
  }

  const days = log.newState?.days || (log.newState?.endsAt && log.previousState?.endsAt
    ? Math.round((new Date(log.newState.endsAt).getTime() - new Date(log.previousState.endsAt).getTime()) / (24 * 60 * 60 * 1000))
    : null);
  const daysText = days ? ` by ${days} days` : '';
  const eventLabel = log.eventType === 'subscription_extended_admin' ? 'Subscription extended' : log.eventType === 'trial_extended_admin' ? 'Trial extended' : 'Trial regranted';
  
  return (
    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
      {eventLabel} <Box component="span" sx={{ fontWeight: 600 }}>{daysText}</Box>. Reason: <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{log.newState?.reason || 'N/A'}</Box>
    </Typography>
  );
};

const renderGoodwillDetails = (log: any) => {
  return (
    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
      Added <Box component="span" sx={{ fontWeight: 600 }}>{log.newState?.amount || 'N/A'} {log.newState?.resourceType || 'credits'}</Box>. Reason: <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{log.newState?.reason || 'N/A'}</Box>
    </Typography>
  );
};

const UserSubscriptions = () => {
  const [params, setParams] = useState({
    ...defaultParams,
    page: 1,
    search: '',
    status: '',
    sortBy: 'createdAt',
    sortOrder: 'DESC' as 'ASC' | 'DESC',
    startDate: '',
    endDate: '',
  });

  const { data, isLoading, isFetching, error, refetch } = useUserSubscriptionsQuery({
    params: {
      page: params.page,
      limit: params.pageSize,
      search: params.search || undefined,
      status: params.status || undefined,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
      startDate: params.startDate || undefined,
      endDate: params.endDate || undefined,
    },
    config: { keepPreviousData: true },
  });

  const subscriptions: UserSubscriptionDto[] = useMemo(() => data?.data || [], [data?.data]);
  const meta = data?.meta;

  const { data: plansData } = useAdminPlansQuery({ config: { keepPreviousData: true } });
  const plans: SubscriptionPlanDto[] = useMemo(() => plansData?.data || [], [plansData?.data]);
  const { data: stats } = useAdminStatsQuery();

  // Modal controls
  const manageModal = useModalDisclosure();
  const assignModal = useModalDisclosure();
  const goodwillModal = useModalDisclosure();
  const extendSubModal = useModalDisclosure();
  const extendTrialModal = useModalDisclosure();
  const grantTrialAgainModal = useModalDisclosure();
  const suspendModal = useModalDisclosure();

  // Selected subscription for management
  const selectedSub = manageModal.data as UserSubscriptionDto | undefined;
  const selectedUserId = selectedSub?.userId || 0;

  // Selected sub lazy queries
  const { data: userSpending } = useUserSpendingQuery({ userId: selectedUserId });
  const { data: userInvoices, isLoading: invoicesLoading } = useUserInvoicesQuery({ userId: selectedUserId });
  const { data: userHistory } = useUserSubscriptionHistoryQuery({ userId: selectedUserId });
  const { data: userUsage } = useUserUsageQuery({ userId: selectedUserId });

  // Form states
  const [assignPlanId, setAssignPlanId] = useState<number | ''>('');
  const [assignEndsAt, setAssignEndsAt] = useState<string>('');
  const [assignReason, setAssignReason] = useState<string>('');
  const [goodwillType, setGoodwillType] = useState<'minutes' | 'sms' | 'agents' | 'sms_numbers'>('minutes');
  const [goodwillAmount, setGoodwillAmount] = useState<string>('');
  const [goodwillReason, setGoodwillReason] = useState<string>('');
  const [extendSubDays, setExtendSubDays] = useState<string>('');
  const [extendSubReason, setExtendSubReason] = useState<string>('');
  const [extendTrialDays, setExtendTrialDays] = useState<string>('');
  const [extendTrialReason, setExtendTrialReason] = useState<string>('');
  const [grantTrialDays, setGrantTrialDays] = useState<string>('');
  const [grantTrialReason, setGrantTrialReason] = useState<string>('');

  const [activeTab, setActiveTab] = useState(0);

  const [invoicesPage, setInvoicesPage] = useState(1);
  const invoicesPerPage = 10;
  const [extPage, setExtPage] = useState(1);
  const extPerPage = 10;
  const [goodwillPage, setGoodwillPage] = useState(1);
  const goodwillPerPage = 10;

  const [extStartDate, setExtStartDate] = useState<string>('');
  const [extEndDate, setExtEndDate] = useState<string>('');
  const [goodwillStartDate, setGoodwillStartDate] = useState<string>('');
  const [goodwillEndDate, setGoodwillEndDate] = useState<string>('');
  const [invoiceStartDate, setInvoiceStartDate] = useState<string>('');
  const [invoiceEndDate, setInvoiceEndDate] = useState<string>('');
  const [invoiceSearch, setInvoiceSearch] = useState<string>('');

  const filteredInvoices = useMemo(() => {
    if (!userInvoices) return [];
    return userInvoices.filter((inv: any) => {
      const invDate = dayjs(inv.created * 1000).format('YYYY-MM-DD');
      if (invoiceStartDate && invDate < invoiceStartDate) return false;
      if (invoiceEndDate && invDate > invoiceEndDate) return false;
      if (invoiceSearch) {
        const term = invoiceSearch.toLowerCase();
        const invNumber = (inv.number || '').toLowerCase();
        const planName = getPlanNameFromInvoice(inv).toLowerCase();
        const status = (inv.status || '').toLowerCase();

        if (!invNumber.includes(term) && !planName.includes(term) && !status.includes(term)) {
          return false;
        }
      }
      return true;
    });
  }, [userInvoices, invoiceStartDate, invoiceEndDate, invoiceSearch]);

  const paginatedInvoices = useMemo(() => {
    const start = (invoicesPage - 1) * invoicesPerPage;
    return filteredInvoices.slice(start, start + invoicesPerPage);
  }, [filteredInvoices, invoicesPage]);

  const extensionLogs = useMemo(() => {
    if (!userHistory?.auditLogs) return [];
    return userHistory.auditLogs.filter((log: any) =>
      ['subscription_extended_admin', 'trial_extended_admin', 'trial_regranted_admin', 'subscription_assigned_admin'].includes(log.eventType)
    );
  }, [userHistory?.auditLogs]);

  const filteredExtensionLogs = useMemo(() => {
    if (!extensionLogs) return [];
    return extensionLogs.filter((log: any) => {
      const logDate = dayjs(log.createdAt).format('YYYY-MM-DD');
      if (extStartDate && logDate < extStartDate) return false;
      if (extEndDate && logDate > extEndDate) return false;
      return true;
    });
  }, [extensionLogs, extStartDate, extEndDate]);

  const goodwillLogs = useMemo(() => {
    if (!userHistory?.auditLogs) return [];
    return userHistory.auditLogs.filter((log: any) =>
      log.eventType === 'goodwill_credits_granted'
    );
  }, [userHistory?.auditLogs]);

  const filteredGoodwillLogs = useMemo(() => {
    if (!goodwillLogs) return [];
    return goodwillLogs.filter((log: any) => {
      const logDate = dayjs(log.createdAt).format('YYYY-MM-DD');
      if (goodwillStartDate && logDate < goodwillStartDate) return false;
      if (goodwillEndDate && logDate > goodwillEndDate) return false;
      return true;
    });
  }, [goodwillLogs, goodwillStartDate, goodwillEndDate]);

  const paginatedExtensionLogs = useMemo(() => {
    const start = (extPage - 1) * extPerPage;
    return filteredExtensionLogs.slice(start, start + extPerPage);
  }, [filteredExtensionLogs, extPage, extPerPage]);

  const paginatedGoodwillLogs = useMemo(() => {
    const start = (goodwillPage - 1) * goodwillPerPage;
    return filteredGoodwillLogs.slice(start, start + goodwillPerPage);
  }, [filteredGoodwillLogs, goodwillPage, goodwillPerPage]);

  useEffect(() => {
    setInvoicesPage(1);
    setExtPage(1);
    setGoodwillPage(1);
    setExtStartDate('');
    setExtEndDate('');
    setGoodwillStartDate('');
    setGoodwillEndDate('');
    setInvoiceStartDate('');
    setInvoiceEndDate('');
    setInvoiceSearch('');
  }, [selectedUserId]);

  // Mutations
  const assignMutation = useAssignPlanToUserMutation({
    onSuccess: () => {
      success('Plan changed successfully');
      refetch();
      assignModal.close();
      setAssignPlanId('');
      setAssignEndsAt('');
      setAssignReason('');
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to change plan');
    },
  });

  const reactivateMutation = useReactivateUserSubscriptionMutation({
    onSuccess: () => {
      success('Subscription reactivated');
      refetch();
      if (selectedSub) {
        // Refresh selected details
        refetch().then((res) => {
          const freshSub = res.data?.data?.find((s: any) => s.id === selectedSub.id);
          if (freshSub) manageModal.open(freshSub);
        });
      }
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to reactivate');
    },
  });

  const suspendMutation = useSuspendUserSubscriptionMutation({
    onSuccess: () => {
      success('Subscription suspended');
      suspendModal.close();
      refetch();
      if (selectedSub) {
        refetch().then((res) => {
          const freshSub = res.data?.data?.find((s: any) => s.id === selectedSub.id);
          if (freshSub) manageModal.open(freshSub);
        });
      }
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to suspend');
    },
  });

  const grantGoodwillMutation = useGrantGoodwillCreditsMutation({
    onSuccess: () => {
      success('Goodwill credits granted successfully');
      goodwillModal.close();
      setGoodwillAmount('');
      setGoodwillReason('');
      refetch();
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to grant goodwill credits');
    }
  });

  const extendSubMutation = useExtendSubscriptionMutation({
    onSuccess: () => {
      success('Subscription extended successfully');
      extendSubModal.close();
      setExtendSubDays('');
      setExtendSubReason('');
      refetch();
      if (selectedSub) {
        refetch().then((res) => {
          const freshSub = res.data?.data?.find((s: any) => s.id === selectedSub.id);
          if (freshSub) manageModal.open(freshSub);
        });
      }
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to extend subscription');
    }
  });

  const extendTrialMutation = useExtendTrialMutation({
    onSuccess: () => {
      success('Trial extended successfully');
      extendTrialModal.close();
      setExtendTrialDays('');
      setExtendTrialReason('');
      refetch();
      if (selectedSub) {
        refetch().then((res) => {
          const freshSub = res.data?.data?.find((s: any) => s.id === selectedSub.id);
          if (freshSub) manageModal.open(freshSub);
        });
      }
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to extend trial');
    }
  });

  const grantTrialAgainMutation = useGrantTrialAgainMutation({
    onSuccess: () => {
      success('Trial granted again successfully');
      grantTrialAgainModal.close();
      setGrantTrialDays('');
      setGrantTrialReason('');
      refetch();
      if (selectedSub) {
        refetch().then((res) => {
          const freshSub = res.data?.data?.find((s: any) => s.id === selectedSub.id);
          if (freshSub) manageModal.open(freshSub);
        });
      }
    },
    onError: (e: any) => {
      failed(e?.response?.data?.message || 'Failed to grant trial again');
    }
  });

  const handlePageChange = (page: number) => {
    setParams((prev) => ({ ...prev, page }));
  };

  const handleSort = (field: string) => {
    const isAsc = params.sortBy === field && params.sortOrder === 'ASC';
    setParams((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: isAsc ? 'DESC' : 'ASC',
      page: 1
    }));
  };

  const handleManage = (sub: UserSubscriptionDto) => {
    setActiveTab(0);
    manageModal.open(sub);
  };

  const openAssign = (sub: UserSubscriptionDto) => {
    assignModal.open(sub);
    setAssignPlanId(sub.planId);
    setAssignEndsAt(sub.endsAt ? dayjs(sub.endsAt).format('YYYY-MM-DD') : '');
  };

  const submitAssign = () => {
    const sub = assignModal.data as UserSubscriptionDto;
    if (!sub?.userId || assignPlanId === '') return;

    assignMutation.mutate({
      userId: sub.userId,
      planId: Number(assignPlanId),
      endsAt: assignEndsAt ? new Date(assignEndsAt).toISOString() : undefined,
      reason: assignReason || undefined
    });
  };

  const handleToggleSuspend = (sub: UserSubscriptionDto) => {
    if (sub.status === 'suspended') {
      reactivateMutation.mutate({ userId: sub.userId });
    } else {
      suspendModal.open(sub);
    }
  };

  const submitSuspend = () => {
    const sub = (suspendModal.data as UserSubscriptionDto) || selectedSub;
    if (sub?.userId) {
      suspendMutation.mutate({ userId: sub.userId });
    }
  };

  const submitGoodwill = () => {
    if (!selectedUserId || !goodwillAmount) return;
    grantGoodwillMutation.mutate({
      userId: selectedUserId,
      resourceType: goodwillType,
      amount: Number(goodwillAmount),
      reason: goodwillReason
    });
  };

  const submitExtendSub = () => {
    if (!selectedUserId || !extendSubDays) return;
    extendSubMutation.mutate({
      userId: selectedUserId,
      days: Number(extendSubDays),
      reason: extendSubReason
    });
  };

  const submitExtendTrial = () => {
    if (!selectedUserId || !extendTrialDays) return;
    extendTrialMutation.mutate({
      userId: selectedUserId,
      days: Number(extendTrialDays),
      reason: extendTrialReason
    });
  };

  const submitGrantTrialAgain = () => {
    if (!selectedUserId || !grantTrialDays) return;
    grantTrialAgainMutation.mutate({
      userId: selectedUserId,
      days: Number(grantTrialDays),
      reason: grantTrialReason
    });
  };

  if (error) {
    return (
      <div className="main-dashboard-div">
        <div className="dashboard-wrapper">
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            Failed to load subscriptions.
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="main-dashboard-div">
      <div className="dashboard-wrapper">
        <div className="heading-dashboard" style={{ marginBottom: '24px' }}>
          <h2>User Subscriptions</h2>
        </div>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ borderRadius: '12px', bgcolor: 'rgba(25, 118, 210, 0.03)', borderColor: 'rgba(25, 118, 210, 0.15)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'rgba(25, 118, 210, 0.1)', color: '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DollarSign size={22} />
                </Box>
                <Box>
                  <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Total Amount
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                    ${stats?.totalRevenue?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ borderRadius: '12px', bgcolor: 'rgba(46, 125, 50, 0.03)', borderColor: 'rgba(46, 125, 50, 0.15)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'rgba(46, 125, 50, 0.1)', color: '#2e7d32', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <UserCheck size={22} />
                </Box>
                <Box>
                  <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Total Active Plan
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {stats?.active || 0}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ borderRadius: '12px', bgcolor: 'rgba(237, 108, 2, 0.03)', borderColor: 'rgba(237, 108, 2, 0.15)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'rgba(237, 108, 2, 0.1)', color: '#ed6c02', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Hourglass size={22} />
                </Box>
                <Box>
                  <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Total Trial Plan
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {stats?.trial || 0}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ borderRadius: '12px', bgcolor: 'rgba(156, 39, 176, 0.03)', borderColor: 'rgba(156, 39, 176, 0.15)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'rgba(156, 39, 176, 0.1)', color: '#9c27b0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Award size={22} />
                </Box>
                <Box>
                  <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Total Plan
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {stats?.totalSubscribers || 0}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          {/* Left side: Filter Status and Dates */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
            <TextField
              select
              size="small"
              label="Filter Status"
              value={params.status}
              onChange={(e) => setParams((prev) => ({ ...prev, status: e.target.value, page: 1 }))}
              sx={{ width: '180px', bgcolor: 'white' }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>

            <TextField
              type="date"
              size="small"
              label="Start Date"
              value={params.startDate || ''}
              onChange={(e) => setParams((prev) => ({ ...prev, startDate: e.target.value, page: 1 }))}
              InputLabelProps={{ shrink: true }}
              sx={{ bgcolor: 'white' }}
            />

            <TextField
              type="date"
              size="small"
              label="End Date"
              value={params.endDate || ''}
              onChange={(e) => setParams((prev) => ({ ...prev, endDate: e.target.value, page: 1 }))}
              InputLabelProps={{ shrink: true }}
              sx={{ bgcolor: 'white' }}
            />
          </Stack>

          {/* Right side: Search Box */}
          <div className="search-box" style={{ width: '320px' }}>
            <input
              type="text"
              placeholder="Search by email, name or Stripe ID"
              className="search-input"
              value={params.search || ''}
              onChange={(e) => setParams((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
            />
          </div>
        </Box>

        <TableContainer component={Paper} sx={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', borderRadius: '12px' }}>
          {isLoading || isFetching ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography>Loading subscriptions...</Typography>
            </Box>
          ) : subscriptions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No subscriptions found</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>
                    <TableSortLabel
                      active={params.sortBy === 'email'}
                      direction={params.sortBy === 'email' ? (params.sortOrder.toLowerCase() as 'asc' | 'desc') : 'asc'}
                      onClick={() => handleSort('email')}
                    >
                      User
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={params.sortBy === 'plan'}
                      direction={params.sortBy === 'plan' ? (params.sortOrder.toLowerCase() as 'asc' | 'desc') : 'asc'}
                      onClick={() => handleSort('plan')}
                    >
                      Plan
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={params.sortBy === 'status'}
                      direction={params.sortBy === 'status' ? (params.sortOrder.toLowerCase() as 'asc' | 'desc') : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={params.sortBy === 'startedAt'}
                      direction={params.sortBy === 'startedAt' ? (params.sortOrder.toLowerCase() as 'asc' | 'desc') : 'asc'}
                      onClick={() => handleSort('startedAt')}
                    >
                      Start Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={params.sortBy === 'endsAt'}
                      direction={params.sortBy === 'endsAt' ? (params.sortOrder.toLowerCase() as 'asc' | 'desc') : 'asc'}
                      onClick={() => handleSort('endsAt')}
                    >
                      Expiry Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Remaining Days</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions.map((sub) => {
                  const userName = sub.user ? `${sub.user.firstname || ''} ${sub.user.lastname || ''}`.trim() : '';
                  const userEmail = sub.user?.email || `User #${sub.userId}`;
                  const planLabel = sub.plan ? `${sub.plan.name} (${sub.plan.code})` : `Plan #${sub.planId}`;

                  const remainingDays = sub.endsAt
                    ? Math.max(0, dayjs(sub.endsAt).diff(dayjs(), 'day'))
                    : '-';

                  return (
                    <TableRow key={sub.id} hover>
                      <TableCell>
                        <div>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{userName || 'No Name'}</Typography>
                          <Typography variant="caption" color="text.secondary">{userEmail}</Typography>
                        </div>
                      </TableCell>
                      <TableCell>{planLabel}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>
                        <Box
                          component="span"
                          sx={{
                            px: 1.5,
                            py: 0.25,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor:
                              sub.status === 'active' || sub.status === 'trial' ? 'rgba(46, 125, 50, 0.1)' :
                              sub.status === 'suspended' ? 'rgba(237, 108, 2, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                            color:
                              sub.status === 'active' || sub.status === 'trial' ? '#2e7d32' :
                              sub.status === 'suspended' ? '#ed6c02' : '#d32f2f',
                          }}
                        >
                          {sub.status}
                        </Box>
                      </TableCell>
                      <TableCell>{sub.startedAt ? dayjs(sub.startedAt).format('MMM DD, YYYY') : '-'}</TableCell>
                      <TableCell>{sub.endsAt ? dayjs(sub.endsAt).format('MMM DD, YYYY') : '-'}</TableCell>
                      <TableCell sx={{ fontWeight: remainingDays !== '-' && remainingDays <= 5 ? 700 : undefined, color: remainingDays !== '-' && remainingDays <= 5 ? '#d32f2f' : undefined }}>
                        {remainingDays}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="outlined" onClick={() => handleManage(sub)}>
                            Manage
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {!isLoading && subscriptions.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <CustomPagination
              totalPages={meta?.totalPages || 1}
              page={meta?.currentPage || params.page}
              handlePage={handlePageChange}
            />
          </Box>
        )}

        {/* 1. Large Manage Modal */}
        <Dialog open={manageModal.isOpen} onClose={manageModal.close} maxWidth="md" fullWidth>
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Manage Subscription: {selectedSub?.user?.email}
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.25,
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: selectedSub?.status === 'active' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  color: selectedSub?.status === 'active' ? '#2e7d32' : 'text.secondary'
                }}
              >
                {selectedSub?.status}
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
                <Tab icon={<UserCheck size={16} />} iconPosition="start" label="Overview & Actions" />
                <Tab icon={<Receipt size={16} />} iconPosition="start" label="Stripe Invoices" />
                <Tab icon={<History size={16} />} iconPosition="start" label="Extensions" />
                <Tab icon={<Award size={16} />} iconPosition="start" label="Goodwill" />
              </Tabs>
            </Box>

            {/* TAB 0: Overview & Actions */}
            <CustomTabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" variant="caption">PLAN DETAILS</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>{selectedSub?.plan?.name}</Typography>
                      <Typography variant="body2" color="text.secondary">Code: {selectedSub?.plan?.code}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" variant="caption">TOTAL AMOUNT SPENT</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, color: '#2e7d32' }}>
                        ${userSpending?.toFixed(2) || '0.00'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Successful charges minus refunds</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Usage Section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Subscription Usage & Credits</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ bgcolor: 'rgba(25, 118, 210, 0.02)' }}>
                        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                          <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 600 }}>AI AGENT SLOTS</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {userUsage?.usage?.agentsCount ?? 0} / {userUsage?.plan?.maxAgents === null ? '∞' : (userUsage?.plan?.maxAgents ?? 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Agents created vs plan limit</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ bgcolor: 'rgba(46, 125, 50, 0.02)' }}>
                        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                          <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 600 }}>VOICE MINUTES</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {userUsage?.usage?.minutesUsed ?? 0} / {userUsage?.plan?.maxMinutesPerMonth === null ? '∞' : (userUsage?.plan?.maxMinutesPerMonth ?? 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Minutes called vs plan limit</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ bgcolor: 'rgba(156, 39, 176, 0.02)' }}>
                        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                          <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 600 }}>SMS COUNT</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {userUsage?.usage?.smsCount ?? 0} / {userUsage?.plan?.maxSmsPerMonth === null ? '∞' : (userUsage?.plan?.maxSmsPerMonth ?? 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">SMS sent vs plan limit</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ bgcolor: 'rgba(237, 108, 2, 0.02)' }}>
                        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                          <Typography color="text.secondary" variant="caption" sx={{ fontWeight: 600 }}>SMS NUMBERS</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {userUsage?.usage?.totalSmsNumbersPurchased ?? userUsage?.usage?.smsNumbersCount ?? 0} / {userUsage?.plan?.smsCapableNumberLimit === null ? '∞' : (userUsage?.plan?.smsCapableNumberLimit ?? 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">SMS numbers vs plan limit</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Agents List & Performance Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>User AI Agents & Call Performance</Typography>
                  {!userUsage?.agents || userUsage.agents.length === 0 ? (
                    <Typography color="text.secondary" variant="body2" sx={{ fontStyle: 'italic', py: 1 }}>
                      No AI agents found for this user.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Agent Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>VAPI ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">Total Calls</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">Total Duration</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userUsage.agents.map((agent: any) => (
                            <TableRow key={agent.id} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                              <TableCell sx={{ py: 1, fontWeight: 500 }}>{agent.name}</TableCell>
                              <TableCell sx={{ py: 1, fontSize: '0.8rem', color: 'text.secondary' }}>{agent.vapiId}</TableCell>
                              <TableCell sx={{ py: 1 }} align="right">{agent.callsCount}</TableCell>
                              <TableCell sx={{ py: 1 }} align="right">{agent.totalDurationMinutes} mins</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Admin Actions</Typography>
                  <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CreditCard size={14} />}
                      onClick={() => selectedSub && openAssign(selectedSub)}
                    >
                      Change Plan
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color={selectedSub?.status === 'suspended' ? 'success' : 'warning'}
                      startIcon={selectedSub?.status === 'suspended' ? <Play size={14} /> : <Pause size={14} />}
                      onClick={() => selectedSub && handleToggleSuspend(selectedSub)}
                    >
                      {selectedSub?.status === 'suspended' ? 'Reactivate' : 'Pause Subscription'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Award size={14} />}
                      onClick={() => goodwillModal.open()}
                    >
                      Grant Goodwill Quota
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Calendar size={14} />}
                      onClick={() => extendSubModal.open()}
                    >
                      Extend Expiry
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Hourglass size={14} />}
                      onClick={() => extendTrialModal.open()}
                      disabled={selectedSub?.status !== 'trial'}
                    >
                      Extend Trial
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UserCheck size={14} />}
                      onClick={() => grantTrialAgainModal.open()}
                    >
                      Grant Trial Again
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CustomTabPanel>

            {/* TAB 1: Stripe Invoices */}
            <CustomTabPanel value={activeTab} index={1}>
              {invoicesLoading ? (
                <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
              ) : !userInvoices || userInvoices.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No invoices found for this subscriber.</Typography>
              ) : (
                <>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, mt: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Receipt size={18} style={{ color: '#1976d2' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Stripe Invoices History
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TextField
                        size="small"
                        placeholder="Search invoice #, plan, status..."
                        value={invoiceSearch}
                        onChange={(e) => {
                          setInvoiceSearch(e.target.value);
                          setInvoicesPage(1);
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search size={16} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: 220 }}
                      />
                      <TextField
                        size="small"
                        type="date"
                        label="Start Date"
                        InputLabelProps={{ shrink: true }}
                        value={invoiceStartDate}
                        onChange={(e) => {
                          setInvoiceStartDate(e.target.value);
                          setInvoicesPage(1);
                        }}
                        sx={{ width: 140 }}
                      />
                      <TextField
                        size="small"
                        type="date"
                        label="End Date"
                        InputLabelProps={{ shrink: true }}
                        value={invoiceEndDate}
                        onChange={(e) => {
                          setInvoiceEndDate(e.target.value);
                          setInvoicesPage(1);
                        }}
                        sx={{ width: 140 }}
                      />
                      {(invoiceStartDate || invoiceEndDate || invoiceSearch) && (
                        <Button size="small" onClick={() => {
                          setInvoiceStartDate('');
                          setInvoiceEndDate('');
                          setInvoiceSearch('');
                        }}>
                          Clear
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  {filteredInvoices.length === 0 ? (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: 'center',
                        border: '1px dashed rgba(0,0,0,0.12)',
                        borderRadius: '12px',
                        bgcolor: 'rgba(0,0,0,0.01)'
                      }}
                    >
                      <Receipt size={32} style={{ color: 'rgba(0,0,0,0.25)', marginBottom: '8px' }} />
                      <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500 }}>
                        No invoices found matching the filters.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell>Invoice Number</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell>Plan</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Download PDF</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedInvoices.map((inv: any) => (
                            <TableRow key={inv.id}>
                              <TableCell sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{inv.number || 'Draft/N/A'}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{dayjs(inv.created * 1000).format('MMM DD, YYYY')}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'capitalize' }}>
                                {getPlanNameFromInvoice(inv)}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{inv.currency?.toUpperCase()} ${(inv.amount_paid / 100).toFixed(2)}</TableCell>
                              <TableCell>
                                <Box
                                  component="span"
                                  sx={{
                                    px: 1.5,
                                    py: 0.25,
                                    borderRadius: '12px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: inv.status === 'paid' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                                    color: inv.status === 'paid' ? '#2e7d32' : 'text.secondary'
                                  }}
                                >
                                  {inv.status}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                {inv.invoice_pdf ? (
                                  <IconButton href={inv.invoice_pdf} target="_blank" size="small" color="primary">
                                    <Download size={16} />
                                  </IconButton>
                                ) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {filteredInvoices.length > invoicesPerPage && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <CustomPagination
                        totalPages={Math.ceil(filteredInvoices.length / invoicesPerPage)}
                        page={invoicesPage}
                        handlePage={(p) => setInvoicesPage(p)}
                      />
                    </Box>
                  )}
                </>
              )}
            </CustomTabPanel>

            {/* TAB 2: Extensions */}
            <CustomTabPanel value={activeTab} index={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <History size={18} style={{ color: '#1976d2' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Subscription Extensions & Plan Changes
                  </Typography>
                </Stack>
                {extensionLogs.length > 0 && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TextField
                      size="small"
                      type="date"
                      label="Start Date"
                      InputLabelProps={{ shrink: true }}
                      value={extStartDate}
                      onChange={(e) => {
                        setExtStartDate(e.target.value);
                        setExtPage(1);
                      }}
                      sx={{ width: 140 }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="End Date"
                      InputLabelProps={{ shrink: true }}
                      value={extEndDate}
                      onChange={(e) => {
                        setExtEndDate(e.target.value);
                        setExtPage(1);
                      }}
                      sx={{ width: 140 }}
                    />
                    {(extStartDate || extEndDate) && (
                      <Button size="small" onClick={() => {
                        setExtStartDate('');
                        setExtEndDate('');
                      }}>
                        Clear
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>

              {extensionLogs.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    border: '1px dashed rgba(0,0,0,0.12)',
                    borderRadius: '12px',
                    bgcolor: 'rgba(0,0,0,0.01)'
                  }}
                >
                  <History size={32} style={{ color: 'rgba(0,0,0,0.25)', marginBottom: '8px' }} />
                  <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500 }}>
                    No subscription extensions or plan changes recorded.
                  </Typography>
                </Box>
              ) : filteredExtensionLogs.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    border: '1px dashed rgba(0,0,0,0.12)',
                    borderRadius: '12px',
                    bgcolor: 'rgba(0,0,0,0.01)'
                  }}
                >
                  <Typography color="text.secondary" variant="body2">
                    No records found for the selected date range.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedExtensionLogs.map((log: any) => {
                          const dateFormatted = dayjs(log.createdAt).format('MMM DD, YYYY HH:mm');
                          return (
                            <TableRow key={log.id} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                              <TableCell sx={{ py: 1.5 }}>
                                {getEventChip(log.eventType)}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary', py: 1.5 }}>{dateFormatted}</TableCell>
                              <TableCell sx={{ py: 1.5 }}>{renderDetails(log, plans)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {filteredExtensionLogs.length > extPerPage && (
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                      <CustomPagination
                        totalPages={Math.ceil(filteredExtensionLogs.length / extPerPage)}
                        page={extPage}
                        handlePage={(p) => setExtPage(p)}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </CustomTabPanel>

            {/* TAB 3: Goodwill */}
            <CustomTabPanel value={activeTab} index={3}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Award size={18} style={{ color: '#9c27b0' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Goodwill Quota Credits History
                  </Typography>
                </Stack>
                {goodwillLogs.length > 0 && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TextField
                      size="small"
                      type="date"
                      label="Start Date"
                      InputLabelProps={{ shrink: true }}
                      value={goodwillStartDate}
                      onChange={(e) => {
                        setGoodwillStartDate(e.target.value);
                        setGoodwillPage(1);
                      }}
                      sx={{ width: 140 }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="End Date"
                      InputLabelProps={{ shrink: true }}
                      value={goodwillEndDate}
                      onChange={(e) => {
                        setGoodwillEndDate(e.target.value);
                        setGoodwillPage(1);
                      }}
                      sx={{ width: 140 }}
                    />
                    {(goodwillStartDate || goodwillEndDate) && (
                      <Button size="small" onClick={() => {
                        setGoodwillStartDate('');
                        setGoodwillEndDate('');
                      }}>
                        Clear
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>

              {goodwillLogs.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    border: '1px dashed rgba(0,0,0,0.12)',
                    borderRadius: '12px',
                    bgcolor: 'rgba(0,0,0,0.01)',
                  }}
                >
                  <Award size={32} style={{ color: 'rgba(0,0,0,0.25)', marginBottom: '8px' }} />
                  <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500 }}>
                    No goodwill credits have been granted to this user yet.
                  </Typography>
                </Box>
              ) : filteredGoodwillLogs.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    border: '1px dashed rgba(0,0,0,0.12)',
                    borderRadius: '12px',
                    bgcolor: 'rgba(0,0,0,0.01)'
                  }}
                >
                  <Typography color="text.secondary" variant="body2">
                    No records found for the selected date range.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedGoodwillLogs.map((log: any) => {
                          const dateFormatted = dayjs(log.createdAt).format('MMM DD, YYYY HH:mm');
                          return (
                            <TableRow key={log.id} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                              <TableCell sx={{ py: 1.5 }}>
                                <Chip
                                  label="Goodwill Credits"
                                  color="secondary"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    borderRadius: '6px',
                                    height: '24px'
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary', py: 1.5 }}>{dateFormatted}</TableCell>
                              <TableCell sx={{ py: 1.5 }}>{renderGoodwillDetails(log)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {filteredGoodwillLogs.length > goodwillPerPage && (
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                      <CustomPagination
                        totalPages={Math.ceil(filteredGoodwillLogs.length / goodwillPerPage)}
                        page={goodwillPage}
                        handlePage={(p) => setGoodwillPage(p)}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </CustomTabPanel>
          </DialogContent>
          <DialogActions>
            <Button onClick={manageModal.close}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* 2. Change Plan Dialog */}
        <Dialog open={assignModal.isOpen} onClose={assignModal.close} maxWidth="sm" fullWidth>
          <DialogTitle>Change Plan</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <TextField
                select
                label="Plan"
                value={assignPlanId}
                onChange={(e) => setAssignPlanId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                {plans.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} ({p.code})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Ends At (optional)"
                type="date"
                value={assignEndsAt}
                onChange={(e) => setAssignEndsAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Reason"
                multiline
                rows={2}
                value={assignReason}
                onChange={(e) => setAssignReason(e.target.value)}
                placeholder="Describe the reason for changing/assigning this plan..."
              />
              <Alert severity="info">
                <AlertTitle>Note</AlertTitle>
                Assigning a plan will set the subscription status to <b>active</b> and clear cancellation.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={assignModal.close}>Cancel</Button>
            <Button variant="contained" onClick={submitAssign} disabled={assignMutation.isLoading || assignPlanId === ''}>
              {assignMutation.isLoading ? 'Changing...' : 'Change'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 3. Goodwill Credits Dialog */}
        <Dialog open={goodwillModal.isOpen} onClose={goodwillModal.close} maxWidth="sm" fullWidth>
          <DialogTitle>Grant Goodwill Credits</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <TextField
                select
                label="Resource Type"
                value={goodwillType}
                onChange={(e) => setGoodwillType(e.target.value as any)}
              >
                <MenuItem value="minutes">Voice Minutes</MenuItem>
                <MenuItem value="sms">SMS Count</MenuItem>
                <MenuItem value="agents">AI Agent Count</MenuItem>
                <MenuItem value="sms_numbers">SMS Phone Numbers</MenuItem>
              </TextField>
              <TextField
                label="Credit"
                type="number"
                value={goodwillAmount}
                onChange={(e) => setGoodwillAmount(e.target.value)}
                placeholder="e.g. 100 minutes, 5 agents, or 2 SMS numbers"
              />
              <TextField
                label="Reason"
                multiline
                rows={2}
                value={goodwillReason}
                onChange={(e) => setGoodwillReason(e.target.value)}
                placeholder="Customer satisfaction / compensation..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={goodwillModal.close}>Cancel</Button>
            <Button variant="contained" onClick={submitGoodwill} disabled={grantGoodwillMutation.isLoading || !goodwillAmount}>
              {grantGoodwillMutation.isLoading ? 'Granting...' : 'Grant'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 4. Extend Subscription Dialog */}
        <Dialog open={extendSubModal.isOpen} onClose={extendSubModal.close} maxWidth="sm" fullWidth>
          <DialogTitle>Extend Subscription Expiry</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <TextField
                label="Number of Days"
                type="number"
                value={extendSubDays}
                onChange={(e) => setExtendSubDays(e.target.value)}
                placeholder="e.g. 30"
              />
              <TextField
                label="Reason"
                multiline
                rows={2}
                value={extendSubReason}
                onChange={(e) => setExtendSubReason(e.target.value)}
                placeholder="Support extension / payment issue..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={extendSubModal.close}>Cancel</Button>
            <Button variant="contained" onClick={submitExtendSub} disabled={extendSubMutation.isLoading || !extendSubDays}>
              {extendSubMutation.isLoading ? 'Extending...' : 'Extend'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 5. Extend Trial Dialog */}
        <Dialog open={extendTrialModal.isOpen} onClose={extendTrialModal.close} maxWidth="sm" fullWidth>
          <DialogTitle>Extend Trial Expiry</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <TextField
                label="Number of Days"
                type="number"
                value={extendTrialDays}
                onChange={(e) => setExtendTrialDays(e.target.value)}
                placeholder="e.g. 7"
              />
              <TextField
                label="Reason"
                multiline
                rows={2}
                value={extendTrialReason}
                onChange={(e) => setExtendTrialReason(e.target.value)}
                placeholder="Customer request / evaluation extension..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={extendTrialModal.close}>Cancel</Button>
            <Button variant="contained" onClick={submitExtendTrial} disabled={extendTrialMutation.isLoading || !extendTrialDays}>
              {extendTrialMutation.isLoading ? 'Extending...' : 'Extend'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 6. Grant Trial Again Dialog */}
        <Dialog open={grantTrialAgainModal.isOpen} onClose={grantTrialAgainModal.close} maxWidth="sm" fullWidth>
          <DialogTitle>Grant Trial Again</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <TextField
                label="Trial Duration (Days)"
                type="number"
                value={grantTrialDays}
                onChange={(e) => setGrantTrialDays(e.target.value)}
                placeholder="e.g. 14"
              />
              <TextField
                label="Reason"
                multiline
                rows={2}
                value={grantTrialReason}
                onChange={(e) => setGrantTrialReason(e.target.value)}
                placeholder="Trial reset..."
              />
              <Alert severity="info">
                This will reset the user's subscription back to <b>trial</b> status with the specified window.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={grantTrialAgainModal.close}>Cancel</Button>
            <Button variant="contained" onClick={submitGrantTrialAgain} disabled={grantTrialAgainMutation.isLoading || !grantTrialDays}>
              {grantTrialAgainMutation.isLoading ? 'Granting...' : 'Grant'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 7. Pause Subscription Confirmation Dialog */}
        <Dialog open={suspendModal.isOpen} onClose={suspendModal.close} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'warning.light',
                  color: 'warning.dark',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Pause size={20} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, lineHeight: 1.2 }}>
                  Pause Subscription
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                  Confirm subscription suspension
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ px: 3, py: 1.5 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Are you sure you want to suspend this subscription? <b>Billing collections will be paused on Stripe.</b>
            </Alert>
            {((suspendModal.data as UserSubscriptionDto) || selectedSub) && (
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  User ID: <strong>{((suspendModal.data as UserSubscriptionDto) || selectedSub)?.userId}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Plan: <strong>{((suspendModal.data as UserSubscriptionDto) || selectedSub)?.plan?.name || 'N/A'}</strong>
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={suspendModal.close}
              variant="outlined"
              color="inherit"
              disabled={suspendMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={submitSuspend}
              disabled={suspendMutation.isLoading}
              startIcon={suspendMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <Pause size={16} />}
            >
              {suspendMutation.isLoading ? 'Pausing...' : 'Pause Subscription'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default UserSubscriptions;
